<template>
  <v-bottom-sheet v-if="sheetMode" :value="visible" @input="update">
    <!-- Slots depend on how and where content is rendered
    Left and right slots are the position on the footer not on the popup/sheet.
    appending "Sheet" following the left or right is whether it appears in the sheet or only on the footer -->
    <!-- Includes a footer for ease of use -->
    <template #activator="scope">
      <bm-page-footer>
        <template #left>
          <div class="no-sheet">
            <slot name="left-not-in-sheet" />
            <slot v-if="usesInSheetSlots" name="activator" v-bind="scope">
              <bm-btn
                v-if="activatorBtnLabel"
                :t="activatorBtnLabel"
                :button-type="activatorBtnType"
                :btn-class="activatorBtnClass"
                v-on="scope.on"
              />
            </slot>
          </div>
        </template>
        <div class="no-sheet">
          <slot />
        </div>
      </bm-page-footer>
    </template>
    <v-sheet>
      <div class="sheet-container">
        <slot name="right-in-sheet" />
        <slot name="left-in-sheet" />
        <!-- Default Cancel button is rendered here -->
        <slot name="cancel">
          <bm-btn
            btn-class="mr-2"
            icon
            dark
            button-type="primary"
            data-qa="bottom-sheet-close"
            @click="update(false)"
            ><bm-icon icon="Close" light
          /></bm-btn>
        </slot>
      </div>
    </v-sheet>
  </v-bottom-sheet>
  <!-- If Sheet Mode not set, Content is rendered here -->
  <bm-page-footer v-else>
    <template #left>
      <div class="no-sheet">
        <slot name="left-not-in-sheet" /><slot name="left-in-sheet" />
      </div>
    </template>
    <div class="no-sheet"><slot name="right-in-sheet" /><slot /></div>
  </bm-page-footer>
</template>

<script setup lang="ts">
import type Key from "@/app/types/common/key";
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmPageFooter from "@/components/pages/BmPageFooter.vue";
import { computed, useSlots } from "vue";

interface Props {
  sheetMode?: boolean;
  visible?: boolean;
  activatorBtnLabel?: Key;
  activatorBtnType?: string;
  activatorBtnClass?: string;
}

interface Events {
  (event: "update:visible", value: boolean): void;
}

withDefaults(defineProps<Props>(), {
  sheetMode: false,
  activatorBtnType: "secondary",
});

const emit = defineEmits<Events>();

const slots = useSlots();

const usesInSheetSlots = computed(
  () =>
    !!slots["right-in-sheet"] || !!slots["left-in-sheet"] || !!slots["cancel"]
);

const update = (newValue: boolean) => {
  emit("update:visible", newValue);
};
</script>

<style scoped lang="sass">

.sheet-container
  text-align: center
  padding: 10px
// Add class full-width to maximise child element size
.sheet-container :deep(.full-width)
  width: 100%
  margin: 5px auto

.no-sheet :deep(.full-width), :deep(.more)
  margin: auto 3px
</style>
